import { Flag, InfoOutline } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import { Autocomplete, Box, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { type GridColDef, type GridRenderCellParams } from 'esseal-data-table';
import { titleize } from 'inflection';

import TimeAutocomplete from '@/components/TimeAutocomplete';
import {
  EmploymentStatusEnum,
  type Issue,
  IssueTypeEnum,
  ResourceScheduleStatusEnum,
  type Schedule,
} from '@/graphql/generated/graphql';
import { formatAddress, titleizeCamelCase } from '@/utils/helperFunctions';

const renderCorporateStatusBadge = (value: ResourceScheduleStatusEnum) => {
  switch (value) {
    case ResourceScheduleStatusEnum.Scheduled:
      return (
        <Chip
          icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
          label="Scheduled"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: '#F0FDF4',
            color: '#15803D',
            border: '1px solid #BBF7D0',
            '& .MuiChip-icon': { color: '#15803D' },
          }}
        />
      );
    case ResourceScheduleStatusEnum.Completed:
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
          label="Completed"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: '#ECFDF5',
            color: '#047857',
            border: '1px solid #A7F3D0',
            '& .MuiChip-icon': { color: '#047857' },
          }}
        />
      );
    case ResourceScheduleStatusEnum.Dropped:
      return (
        <Chip
          icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
          label="Dropped"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: '#FEF2F2',
            color: '#B91C1C',
            border: '1px solid #FECACA',
            '& .MuiChip-icon': { color: '#B91C1C' },
          }}
        />
      );
    case ResourceScheduleStatusEnum.Missed:
      return (
        <Chip
          icon={<ErrorIcon sx={{ fontSize: '14px !important' }} />}
          label="Missed"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: '#FFFBEB',
            color: '#B45309',
            border: '1px solid #FDE68A',
            '& .MuiChip-icon': { color: '#B45309' },
          }}
        />
      );
    default:
      return null;
  }
};

export const peopleRegisterColumnsV2 = (timeFormat: string): GridColDef<Schedule>[] => [
  {
    field: 'companyResource',
    headerName: 'Resource Name',
    width: 175,
    valueGetter: (row) => row.companyResource?.name ?? 'No Resource Assigned',
    renderCell: ({ row }) => {
      const isPending =
        row.companyResource?.user && row.companyResource.user.defaultEmploymentStatus === EmploymentStatusEnum.Pending;
      const displayName = row.companyResource ? titleizeCamelCase(row.companyResource.name) : 'No Resource Assigned';

      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A', fontSize: '0.8125rem' }}>
            {displayName}
          </Typography>
          {isPending && (
            <Tooltip title="Employment Pending Verification">
              <Chip
                icon={<InfoOutline sx={{ fontSize: '12px !important' }} />}
                label="Pending"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  backgroundColor: '#FFF1F2',
                  color: '#BE123C',
                  border: '1px solid #FECDD3',
                  '& .MuiChip-icon': { color: '#BE123C' },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      );
    },
  },
  {
    field: 'title',
    headerName: 'Role / Designation',
    width: 140,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.8rem' }}>
        {value || '—'}
      </Typography>
    ),
  },
  {
    field: 'startTime',
    headerName: 'Check In',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'endTime',
    headerName: 'Check Out',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    filterable: false,
    renderCell: ({ value }) => renderCorporateStatusBadge(value as ResourceScheduleStatusEnum),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 105,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
        {value || '—'}
      </Typography>
    ),
  },
  {
    field: 'address',
    headerName: 'Location',
    width: 125,
    valueGetter: (row) => (row.address ? (row.address?.title ?? 'Primary') : '-'),
    renderCell: ({ row, value }) => (
      <Tooltip title={formatAddress(row.address)}>
        <Typography
          variant="caption"
          noWrap
          sx={{
            maxWidth: '120px',
            color: '#334155',
            fontWeight: 500,
            backgroundColor: '#F8FAFC',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid #E2E8F0',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
  },
];

export const equipmentRegisterColumnsV2 = (timeFormat: string): GridColDef<Schedule>[] => [
  {
    field: 'companyResource',
    headerName: 'Equipment Name',
    width: 180,
    valueGetter: (row) => row.companyResource?.name ?? 'No Resource Assigned',
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A', fontSize: '0.8125rem' }}>
        {row.companyResource ? titleizeCamelCase(row.companyResource.name) : 'No Resource Assigned'}
      </Typography>
    ),
  },
  {
    field: 'quantity',
    headerName: 'Qty',
    width: 70,
    renderCell: ({ value }) => (
      <Chip
        label={value ?? 1}
        size="small"
        sx={{
          height: 20,
          fontFamily: 'monospace',
          fontWeight: 700,
          fontSize: '0.75rem',
          backgroundColor: '#F1F5F9',
          color: '#0F172A',
        }}
      />
    ),
  },
  {
    field: 'startTime',
    headerName: 'Check In',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'endTime',
    headerName: 'Check Out',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    filterable: false,
    renderCell: ({ value }) => renderCorporateStatusBadge(value as ResourceScheduleStatusEnum),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 115,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
        {value || '—'}
      </Typography>
    ),
  },
  {
    field: 'address',
    headerName: 'Location',
    width: 125,
    valueGetter: (row) => (row.address ? (row.address?.title ?? 'Primary') : '-'),
    renderCell: ({ row, value }) => (
      <Tooltip title={formatAddress(row.address)}>
        <Typography
          variant="caption"
          noWrap
          sx={{
            maxWidth: '120px',
            color: '#334155',
            fontWeight: 500,
            backgroundColor: '#F8FAFC',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid #E2E8F0',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
  },
];

export const subContractorRegisterColumnsV2 = (timeFormat: string): GridColDef<Schedule>[] => [
  {
    field: 'companyResource',
    headerName: 'Subcontractor',
    width: 175,
    valueGetter: (row) => row.companyResource?.name ?? 'No Resource Assigned',
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A', fontSize: '0.8125rem' }}>
        {row.companyResource ? titleizeCamelCase(row.companyResource.name) : 'No Resource Assigned'}
      </Typography>
    ),
  },
  {
    field: 'pointOfContact',
    headerName: 'Point of Contact',
    width: 145,
    valueGetter: (row) => row.companyResource?.resourceMeta?.pointOfContact ?? 'No POC Assigned',
    renderCell: ({ row }) => (
      <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.8rem' }}>
        {row.companyResource?.resourceMeta?.pointOfContact
          ? titleizeCamelCase(row.companyResource?.resourceMeta?.pointOfContact)
          : '—'}
      </Typography>
    ),
  },
  {
    field: 'startTime',
    headerName: 'Check In',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'endTime',
    headerName: 'Check Out',
    width: 95,
    filterable: false,
    renderCell: (row) => (
      <Typography
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '0.8rem',
        }}
      >
        {dayjs(row.value).format(timeFormat)}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    filterable: false,
    renderCell: ({ value }) => renderCorporateStatusBadge(value as ResourceScheduleStatusEnum),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 105,
    renderCell: ({ value }) => (
      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.8rem' }}>
        {value || '—'}
      </Typography>
    ),
  },
  {
    field: 'address',
    headerName: 'Location',
    width: 125,
    valueGetter: (row) => (row.address ? (row.address?.title ?? 'Primary') : '-'),
    renderCell: ({ row, value }) => (
      <Tooltip title={formatAddress(row.address)}>
        <Typography
          variant="caption"
          noWrap
          sx={{
            maxWidth: '120px',
            color: '#334155',
            fontWeight: 500,
            backgroundColor: '#F8FAFC',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid #E2E8F0',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
  },
];

export const diaryEntryColumnsV2 = (
  handleUpdate: (value: string, field: 'startTime' | 'endTime' | 'issueType' | 'description', id: string) => void,
): GridColDef<Issue>[] => [
  {
    field: 'headline',
    headerName: 'Headline',
    width: 165,
    renderCell: ({ row }: GridRenderCellParams<Issue>) => (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {row.flagged && (
          <Tooltip title="Flagged as Potential Event">
            <Flag sx={{ fontSize: 15, color: '#DC2626' }} />
          </Tooltip>
        )}
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#0F172A', fontSize: '0.8125rem' }}>
          {row.headline || 'Untitled Entry'}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'startTime',
    headerName: 'Start',
    width: 95,
    renderCell: ({ row }: GridRenderCellParams<Issue>) => (
      <TimeAutocomplete
        key={`issue-table-v2-${row.id}-starttime-${row.startTime}`}
        time={row.startTime ? dayjs(row.startTime).format('HH:mm') : ''}
        onTimeChange={(newTime) => handleUpdate(newTime, 'startTime', row.id)}
        sx={{ p: 0, m: 0, width: '100%' }}
      />
    ),
  },
  {
    field: 'endTime',
    headerName: 'End',
    width: 95,
    renderCell: ({ row }: GridRenderCellParams<Issue>) => (
      <TimeAutocomplete
        key={`issue-table-v2-${row.id}-endtime-${row.endTime}`}
        time={row.endTime ? dayjs(row.endTime).format('HH:mm') : ''}
        onTimeChange={(newTime) => handleUpdate(newTime, 'endTime', row.id)}
        sx={{ p: 0, m: 0, width: '100%' }}
      />
    ),
  },
  {
    field: 'issueType',
    headerName: 'Classification',
    width: 150,
    renderCell: ({ row }: GridRenderCellParams<Issue>) => (
      <Autocomplete
        disableClearable
        options={Object.keys(IssueTypeEnum).map((key) => IssueTypeEnum[key as keyof typeof IssueTypeEnum])}
        getOptionLabel={(option) => titleize(option)}
        value={row.issueType}
        size="small"
        sx={{
          px: 1,
          width: '100%',
          height: '26px',
          border: '1px solid #CBD5E1',
          borderRadius: '6px',
          backgroundColor: row.issueType === IssueTypeEnum.ExternalIssue ? '#FFFBEB' : '#EFF6FF',
        }}
        renderInput={(autocompleteParams) => (
          <TextField {...autocompleteParams} variant="standard" sx={{ p: 0, m: 0 }} />
        )}
        onChange={(_, value) => handleUpdate(value, 'issueType', row.id)}
      />
    ),
  },
  {
    field: 'description',
    headerName: 'Diary Entry Details',
    width: 340,
    renderCell: ({ row }: GridRenderCellParams<Issue>) => (
      <Box
        sx={{
          color: '#334155',
          fontSize: '0.8rem',
          '& p': { m: 0 },
          "& span[data-type='mention']": {
            fontWeight: 600,
            backgroundColor: '#DBEAFE',
            color: '#1E40AF',
            px: 0.5,
            py: 0.1,
            borderRadius: '4px',
          },
        }}
        dangerouslySetInnerHTML={{ __html: row.description }}
      />
    ),
  },
];
